import {
  Mail,
  Globe,
  Share2,
  Video,
  Plane,
  GraduationCap,
  Store,
  Bus,
  Handshake,
  Megaphone,
  type LucideIcon,
} from "lucide-react";

export type CampaignType =
  | "email"
  | "google_ads"
  | "meta_ads"
  | "video"
  | "fam_trip"
  | "educational"
  | "trade_show"
  | "roadshow"
  | "trade_marketing"
  | "other";

export const CAMPAIGN_TYPES: { value: CampaignType; label: string; icon: LucideIcon }[] = [
  { value: "email", label: "Email / Newsletter", icon: Mail },
  { value: "google_ads", label: "Google Ads", icon: Globe },
  { value: "meta_ads", label: "Facebook / Instagram Ads", icon: Share2 },
  { value: "video", label: "Video Campaign", icon: Video },
  { value: "fam_trip", label: "FAM Trip", icon: Plane },
  { value: "educational", label: "Educational / Showcase", icon: GraduationCap },
  { value: "trade_show", label: "Trade Show / Expo", icon: Store },
  { value: "roadshow", label: "Roadshow", icon: Bus },
  { value: "trade_marketing", label: "Trade Marketing", icon: Handshake },
  { value: "other", label: "Other", icon: Megaphone },
];

/** Campaign types that get the monthly trade-reports section (retainers, rep work). */
export const TYPES_WITH_MONTHLY_REPORTS: CampaignType[] = ["trade_marketing"];

export type AcquisitionSource = "legacy_coffeebeans" | "bmp_africa" | "campaign" | "other";

export const ACQUISITION_SOURCES: { value: AcquisitionSource; label: string }[] = [
  { value: "legacy_coffeebeans", label: "Legacy Coffeebeans" },
  { value: "bmp_africa", label: "BMP Africa" },
  { value: "campaign", label: "Specific Campaign" },
  { value: "other", label: "Other" },
];

export function acquisitionSourceLabel(value: string | null | undefined): string {
  return ACQUISITION_SOURCES.find((s) => s.value === value)?.label ?? "—";
}

export const CAMPAIGN_STATUSES = ["draft", "active", "paused", "completed", "cancelled"] as const;

export const ATTENDEE_STATUSES = ["invited", "confirmed", "attended", "no_show"] as const;

/** Campaign types that host people — these get the attendee roster. */
export const TYPES_WITH_ATTENDEES: CampaignType[] = ["fam_trip", "educational", "trade_show", "roadshow"];

export function campaignTypeLabel(value: string | null | undefined): string {
  return CAMPAIGN_TYPES.find((t) => t.value === value)?.label ?? "Other";
}

export function campaignTypeIcon(value: string | null | undefined): LucideIcon {
  return CAMPAIGN_TYPES.find((t) => t.value === value)?.icon ?? Megaphone;
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Fallback rates consistent with the rest of the OS (budgetActualUtils).
const USD_TO_ZAR = 18.5;
const EUR_TO_ZAR = 20;

export function toZAR(amount: number, currency: string | null | undefined): number {
  if (!amount) return 0;
  const c = (currency || "ZAR").toUpperCase();
  if (c === "ZAR") return amount;
  if (c === "USD") return amount * USD_TO_ZAR;
  if (c === "EUR") return amount * EUR_TO_ZAR;
  return amount;
}

export function convertCurrency(amount: number, from: string | null | undefined, to: string | null | undefined): number {
  const target = (to || "ZAR").toUpperCase();
  const zar = toZAR(amount, from);
  if (target === "ZAR") return zar;
  if (target === "USD") return zar / USD_TO_ZAR;
  if (target === "EUR") return zar / EUR_TO_ZAR;
  return zar;
}

export function formatMoney(amount: number, currency: string | null | undefined): string {
  const c = (currency || "ZAR").toUpperCase();
  const symbol = c === "USD" ? "$" : c === "EUR" ? "€" : "R";
  return `${symbol}${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
